// Tarjeta de tarifa con el diseño simple del cotizador conversacional IA
// (AIChatQuote): tarjeta horizontal compacta, nombre del carrier, servicio +
// días de tránsito, origen del convenio ("vía …") y precio con CTA. Conserva
// los badges de ranking (más económica / más rápida) y destacado, y el estado
// "No disponible" cuando el convenio no está fondeado. Se usa en el Super Agente
// IA manteniendo todos los filtros y vistas de QuoteResults.
import { getCarrier } from "@/lib/carriers";
import { getAggregatorLabel } from "@/lib/aggregatorLabel";
import { formatMXN } from "@/lib/format";
import UserCurrencyPrice from "@/components/quote/UserCurrencyPrice";
import { computeSavings } from "@/components/quote/SavingsBadge";
import { isFeaturedRate } from "@/lib/featuredRate";
import { Star, Ban, Check } from "lucide-react";
import PickupBadge from "./PickupBadge";
import OriginalCurrencyTooltip from "./OriginalCurrencyTooltip";

export default function SimpleRateCard({ rate, onSelect, selected, selectLabel, funded = true, compact = false }) {
  const c = getCarrier(rate.carrier);
  const origin = getAggregatorLabel(rate);
  const savings = computeSavings(rate);
  const cheapest = rate._rank === "cheapest";
  const fastest = rate._rank === "fastest";
  const featured = isFeaturedRate(rate);
  const pad = compact ? "p-2" : "p-3";
  return (
    <button
      type="button"
      onClick={() => funded && onSelect?.(rate)}
      disabled={!funded}
      aria-label={`${funded ? "Elegir" : "No disponible"} ${c.name} ${rate.service || ""}`}
      className={`w-full ${pad} flex items-center justify-between gap-2 text-left transition-colors ${
        funded ? "surface-inset hover:border-cyan" : "surface-inset opacity-60 cursor-not-allowed"
      } ${selected ? "border-cyan bg-cyan/10 glow-cyan ring-1 ring-cyan" : ""}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-display font-bold text-sm truncate">{c.name}</span>
          {featured && (
            <span className="flex items-center gap-0.5 text-[0.55rem] uppercase font-bold text-violet-500 dark:text-violet-300 bg-violet-500/15 border border-violet-500/50 px-1 py-0.5 shrink-0">
              <Star className="w-2.5 h-2.5 fill-current" /> Destacado
            </span>
          )}
          {cheapest && <span className="text-[0.6rem] uppercase font-bold text-success shrink-0">Econ</span>}
          {fastest && <span className="text-[0.6rem] uppercase font-bold text-cyan shrink-0">Rápida</span>}
        </div>
        <div className="text-[0.7rem] text-ink-muted truncate">
          {rate.service || "Nacional"}{rate.transit_days ? ` · ${rate.transit_days} días` : ""}
        </div>
        {origin && <div className="text-[0.6rem] text-cyan/80 uppercase tracking-wide truncate">vía {origin}</div>}
        <PickupBadge pickup={rate.pickup} size="xs" />
      </div>
      <div className="text-right shrink-0">
        <OriginalCurrencyTooltip rate={rate}>
          <div className={`font-display font-bold text-sm whitespace-nowrap ${featured ? "text-violet-500 dark:text-violet-300" : "text-cyan"} cursor-help`}>
            <UserCurrencyPrice mxn={rate.total_cost} size="xs" showLabel={false} />
          </div>
        </OriginalCurrencyTooltip>
        {savings > 0 && <div className="text-[0.55rem] font-bold text-success leading-tight whitespace-nowrap">−{formatMXN(savings)} vs mostrador</div>}
        {funded ? (
          selected ? (
            <span className="flex items-center gap-1 text-xs font-bold text-cyan"><Check className="w-3 h-3" /> Seleccionada</span>
          ) : (
            <span className="text-xs text-cyan">{selectLabel || "Elegir tarifa"}</span>
          )
        ) : (
          <span className="flex items-center justify-end gap-0.5 text-xs text-destructive">
            <Ban className="w-3 h-3" /> No disponible
          </span>
        )}
      </div>
    </button>
  );
}